import { useAuthStore } from '@tundr/auth';
import { LifecyclePhase, type LifecycleHook } from '@tundr/lifecycle-hooks';
import { useGetCurrentCompany } from '../api/company/company.api.ts';

// vue-request's cancel() leaves the pending runAsync() promise unsettled
// forever, so every cancel must be paired with a SESSION_INIT reset or the
// awaited phase (and the App.vue boot overlay) would never resolve.
let cancelFetch: (() => void) | null = null;

export const companyDetailFetchOp: LifecycleHook = {
  name: 'company:fetch-detail',
  fn: async () => {
    cancelFetch?.();
    cancelFetch = null;
    const authStore = useAuthStore();
    const loginCompany = authStore.selectedCompany;
    if (!loginCompany) return;

    // selectedCompany may carry detail fields merged from a previously
    // selected company (setCompanySelected merges); hydrating from the
    // pristine login payload keeps a failed fetch from leaking them.
    const pristine =
      authStore.userCompanies?.find(
        (company) => company.id === loginCompany.id,
      ) ?? loginCompany;

    const { runAsync, cancel } = useGetCurrentCompany({
      manual: true,
      onSuccess: (response) => {
        if (response.data.id !== loginCompany.id) return;
        authStore.hydrateCompanyDetail({ ...pristine, ...response.data });
      },
      onError: () => authStore.hydrateCompanyDetail(pristine),
    });
    cancelFetch = cancel;
    await runAsync();
  },
  phases: [
    { phase: LifecyclePhase.SESSION_INIT, awaited: true, critical: false },
  ],
};

export const companyDetailResetOp: LifecycleHook = {
  name: 'company:reset-detail',
  fn: () => {
    cancelFetch?.();
    cancelFetch = null;
  },
  phases: [{ phase: LifecyclePhase.LOGOUT }],
};
